import { useMemo } from 'react';
import { useAuthStore } from '@/store/auth.store';
import {
  isGlobalHR as checkGlobalHR,
  isCountryHR as checkCountryHR,
  isStaffOnly as checkStaffOnly,
  canViewEmployeeNav as checkCanViewEmployeeNav,
  getUserCountryFilter,
  hasAnyTalentChekRole as checkHasAnyTalentChekRole,
  isAdmin as checkIsAdmin,
  isCountryManager as checkCountryManager,
} from '@/utils/roles.util';
import {
  parseApprovalLevel,
  isFullTimeEmployee,
} from '@/features/salary-advance/config/salaryAdvanceFlows';

/**
 * React hook that derives TalentChek role flags from the auth store.
 * Also exposes gradeLevel and approvalLevel from the authenticated employee record.
 * Use this in components that need role-based or approval-level rendering decisions.
 */
export function useUserRoles() {
  const authority = useAuthStore(state => state.authority);
  const userProfile = useAuthStore(state => state.userProfile);
  const authenticatedEmployee = useAuthStore(state => state.authenticatedEmployee);

  const roles = authority?.roles ?? [];
  const userCountry = userProfile?.country;

  return useMemo(() => ({
    roles,
    userCountry,
    hasTalentChekRole: checkHasAnyTalentChekRole(roles),
    isGlobalHR: checkGlobalHR(roles),
    isCountryHR: checkCountryHR(roles),
    isCountryManager: checkCountryManager(roles),
    isStaffOnly: checkStaffOnly(roles),
    isAdmin: checkIsAdmin(roles),
    canViewEmployeeNav: checkCanViewEmployeeNav(roles),
    /** Country code to auto-filter data by, or null if no restriction */
    countryFilter: getUserCountryFilter(roles, userCountry),
    /** Employee's grade level (e.g. "P3", "M2") — determines salary advance approval flow */
    gradeLevel: authenticatedEmployee?.gradeLevel ?? null,
    /**
     * Salary advance approval authority level (1–6).
     * Automatically parses backend text formats (e.g. "APPROVAL_LEVEL_ONE") or numbers.
     * null means this employee has no approval authority.
     */
    approvalLevel: parseApprovalLevel(authenticatedEmployee?.approvalLevel),
    /** Employee employment type (e.g. 'Full-Time', 'Contract', etc.) */
    employmentType: authenticatedEmployee?.employmentType ?? null,
    /**
     * Whether the employee has permanent Full-Time status eligible for salary advance.
     * When authenticatedEmployee is present, verifies isFullTimeEmployee.
     */
    isFullTime: authenticatedEmployee
      ? isFullTimeEmployee(authenticatedEmployee.employmentType)
      : true,
  }), [roles, userCountry, authenticatedEmployee]);
}
